import { ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { sectionTrail } from '../../../shared/components/section-trail';
import { buildComplianceOverviewColumns } from '../components/build-compliance-overview-columns';
import { ComplianceOverviewCountTiles } from '../components/ComplianceOverviewCountTiles';
import { ComplianceOverviewTable } from '../components/ComplianceOverviewTable';
import { useComplianceDocumentTypeOptions } from '../hooks/use-compliance-document-type-options';
import { useComplianceOverview } from '../hooks/use-compliance-overview';
import { useComplianceOverviewCounts } from '../hooks/use-compliance-overview-counts';

function CountTilesSkeleton() {
  return (
    <SkeletonRegion label="Loading compliance counts" className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-16 rounded-lg" />
      ))}
    </SkeletonRegion>
  );
}

export function ComplianceOverviewPage() {
  const { state, list, isRefreshing } = useComplianceOverview();
  const countsState = useComplianceOverviewCounts();
  const documentTypeOptions = useComplianceDocumentTypeOptions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const columns = buildComplianceOverviewColumns({ list, documentTypeOptions });

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('compliance')}
        title="Compliance"
        description="Expired, expiring and missing legal documents across the fleet — before dispatch ever sees them."
      />
      {countsState.status === 'loading' && <CountTilesSkeleton />}
      {countsState.status === 'loaded' && <ComplianceOverviewCountTiles counts={countsState.counts} />}
      {state.status === 'error' && (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load the compliance overview"
          description="Something went wrong loading this list. Try reloading the page."
        />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading compliance overview" withSearch />}
      {state.status === 'loaded' && <ComplianceOverviewTable page={state.page} columns={columns} list={list} isRefreshing={isRefreshing} />}
    </>
  );
}
